import React, { CSSProperties } from 'react';
import { Column } from 'react-table';
import { FormattedMessage } from 'react-intl';
import { reduce, filter, any, values, indexOf } from 'ramda';
import { IQuestion } from '../../../../common/models/question/IQuestion';
import { IAttribute } from '../../../../common/models/attribute/IAttribute';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import { DefaultAttributeType } from '../../../../common/enums/DefaultAttributeType';
import { IBindingCallback1, IBindingCallback2 } from '../../../../common/models/callback';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import { getAttributeColors } from '../../../../common/helpers/colorHelper';
import styles from './styles.module.scss';

export const getAttributeCounters = (selected: IQuestion[], attributes: IAttribute[]) => {
  const counters = reduce((acc, elem) => ({ ...acc, [elem.name]: 0 }), {}, attributes);
  return reduce((acc, { subAttribute }) => (subAttribute
    ? { ...acc, [subAttribute.attribute.name]: acc[subAttribute.attribute.name] + 1 }
    : acc), counters as Record<string, number>, selected);
};

export const getRemainingQuestions = (
  questions: IQuestionWithOwner[],
  selectedQuestions: ISurveyQuestion[]
) => (
  filter(({ id }) => !any(({ question }) => question.id === id, selectedQuestions), questions)
);

export const getRemainingQBySubAttribute = (
  subAttributeId: string,
  questions: IQuestionWithOwner[],
  selectedQuestions: ISurveyQuestion[]
) => (
  filter(({ subAttribute, id }) => (
    subAttribute?.id === subAttributeId
    && !any(({ question }) => question.id === id, selectedQuestions)
  ), questions)
);

export const orderByAttribute = (firstQuestion: ISurveyQuestion, secondQuestion: ISurveyQuestion) => {
  const attributes = values(DefaultAttributeType);
  return (indexOf(firstQuestion.question.subAttribute?.attribute.name, attributes)
    - indexOf(secondQuestion.question.subAttribute?.attribute.name, attributes));
};

export const getColumns = (allRemainingQuestions: IQuestionWithOwner[], actions: {
  onRemove: IBindingCallback1<ISurveyQuestion>;
  onReplace: IBindingCallback2<ISurveyQuestion, IQuestionWithOwner[]>;
}): Column[] => [
  {
    Header: () => <FormattedMessage id="question" defaultMessage="Question" />,
    id: 'question',
    Cell: ({ row }) => {
      const { question: { text, lead } } = row.original as ISurveyQuestion;
      return (
        <div>
          <div className="rating-scale-hint">{lead}</div>
          <span className="overflow-wrap">{text}</span>
        </div>
      );
    },
    width: 300
  },
  {
    Header: () => <FormattedMessage id="competency" defaultMessage="Competency" />,
    id: 'competency',
    Cell: ({ row }) => {
      const { question: { subAttribute } } = row.original as ISurveyQuestion;
      const { color, backgroundColor } = getAttributeColors(subAttribute?.attribute.name as string);
      return (
        <div
          className="custom-badge"
          style={{ '--badge-background': backgroundColor, '--badge-color': color } as CSSProperties}
        >
          {subAttribute?.name}
        </div>
      );
    },
    width: 100
  },
  {
    Header: () => <FormattedMessage id="actions" defaultMessage="Actions" />,
    id: 'actions',
    Cell: ({ row }) => {
      const surveyQuestion = row.original as ISurveyQuestion;
      const remainingQuestions = filter(
        ({ subAttribute }) => subAttribute?.id === surveyQuestion.question.subAttribute?.id,
        allRemainingQuestions
      );

      return (
        <div className={styles.actionsColumn}>
          <IconButton
            onClick={() => actions.onReplace(surveyQuestion, remainingQuestions)}
            icon={<Icon icon="exchange" />}
            disabled={!remainingQuestions.length}
            variant="blue"
            tooltip={remainingQuestions.length
              ? { id: 'surveyBuilder.replace', defaultMessage: 'Replace question' }
              : { id: 'surveyBuilder.noQuestions', defaultMessage: 'No questions left' }}
          />
          <IconButton
            onClick={() => actions.onRemove(surveyQuestion)}
            icon={<Icon icon="minusCircle" />}
            variant="blue"
            tooltip={{ id: 'surveyBuilder.remove', defaultMessage: 'Remove question' }}
          />
        </div>
      );
    },
    width: 60
  }
];
